import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, animate } from "framer-motion";
import { ArrowRight, MessageCircle, AlertCircle, ArrowDown } from "lucide-react";
import { HERO } from "../../data/hseData";

/* Reveal a text line from below, masked by its parent */
const Line = ({ children, delay = 0, className = "" }) => (
  <span className={`mask-line ${className}`}>
    <motion.span
      initial={{ y: "110%", rotate: 4 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1], delay }}
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

/* Animated counter for the 20-year mark */
const YearCounter = ({ from = 2006, to = 2026, delay = 0 }) => {
  const value = useMotionValue(from);
  const [display, setDisplay] = useState(from);
  useEffect(() => {
    const controls = animate(value, to, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      delay,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value, to, delay]);
  return <span>{display}</span>;
};

export const Hero = () => {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yType = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      id="home"
      ref={wrapRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#05121F]"
    >
      {/* Background image with slow ken-burns + scroll parallax */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 will-change-transform">
        <img
          src="https://customer-assets.emergentagent.com/job_resgate-industrial/artifacts/9npttm94_16.jpg"
          alt="Operação técnica HSE em altura"
          className="w-full h-full object-cover animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05121F]/70 via-[#05121F]/60 to-[#05121F]" />
        <div className="absolute inset-0 bg-grid opacity-40" />
      </motion.div>

      {/* Massive display "20" - background type */}
      <motion.div
        style={{ y: yType, opacity: fade }}
        className="pointer-events-none select-none absolute inset-0 flex items-center justify-end pr-4 sm:pr-10 lg:pr-16 z-[1]"
      >
        <motion.span
          initial={{ opacity: 0, x: 80 }}
          animate={{ opacity: 0.09, x: 0 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
          className="font-condensed font-bold text-white leading-[0.75] tracking-[-0.06em]"
          style={{ fontSize: "clamp(18rem, 46vw, 44rem)" }}
        >
          20
        </motion.span>
      </motion.div>

      {/* Side rail label */}
      <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 flex-col items-center gap-4 z-10">
        <div className="w-px h-20 bg-white/20" />
        <span
          className="font-condensed uppercase text-[10px] tracking-[0.4em] text-white/50 rotate-180"
          style={{ writingMode: "vertical-rl" }}
        >
          HSE / Critical Services
        </span>
        <div className="w-px h-20 bg-white/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 pt-32 pb-24 sm:pb-32 w-full">
        <div className="max-w-5xl">
          {/* Anniversary meta line */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="flex items-center gap-4 mb-10"
            data-testid="hero-anniversary-badge"
          >
            <span className="w-2 h-2 bg-[#2FA36B] animate-pulse" />
            <span className="font-condensed uppercase text-[11px] tracking-[0.42em] text-white/70 font-semibold">
              HSE · <YearCounter from={2006} to={2006} /> — <YearCounter from={2006} to={2026} delay={0.2} /> · 20 anos
            </span>
            <span className="hidden sm:block flex-1 h-px bg-gradient-to-r from-[#2FA36B]/60 via-white/10 to-transparent" />
          </motion.div>

          <h1 className="font-condensed font-bold text-white uppercase leading-[0.86] tracking-[-0.02em] text-[3.6rem] sm:text-7xl md:text-8xl lg:text-[9rem]">
            <Line delay={0.35}>20 anos</Line>
            <Line delay={0.5} className="text-white/90">protegendo</Line>
            <Line delay={0.65} className="text-[#2FA36B]">vidas, operações</Line>
            <Line delay={0.8} className="text-white/90">&nbsp;&amp; negócios.</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed font-body"
          >
            {HERO.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <a
              href="#contato"
              data-testid="hero-cta-proposta"
              className="group inline-flex items-center justify-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#37B87A] text-white px-7 py-5 transition-all hover:-translate-y-0.5"
            >
              Solicitar proposta
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#contato"
              data-testid="hero-cta-especialista"
              className="inline-flex items-center justify-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold border border-white/30 hover:border-white text-white px-7 py-5 transition-colors"
            >
              <MessageCircle size={18} />
              Falar com especialista
            </a>
            <a
              href="#servicos"
              data-testid="hero-cta-servicos"
              className="inline-flex items-center justify-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold text-white/70 hover:text-white px-7 py-5 transition-colors"
            >
              Conheça nossos serviços
            </a>
          </motion.div>

          {/* Scroll hint */}
          <motion.a
            href="#sobre"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.9 }}
            className="hidden md:inline-flex items-center gap-3 mt-16 font-condensed uppercase text-[11px] tracking-[0.3em] text-white/50 hover:text-white transition-colors"
          >
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex"
            >
              <ArrowDown size={14} />
            </motion.span>
            Rolar
          </motion.a>
        </div>
      </div>

      {/* Emergency strip */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-black/85 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <AlertCircle size={18} className="text-[#2FA36B]" />
            <span className="font-condensed uppercase text-xs sm:text-sm tracking-[0.18em] text-white">
              Atendimento emergencial 24/7 para operações críticas
            </span>
          </div>
          <a
            href="#contato"
            data-testid="hero-cta-emergencia"
            className="font-condensed uppercase text-xs tracking-[0.2em] text-[#2FA36B] hover:text-white font-bold flex items-center gap-2"
          >
            Acionar HSE <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hero;
